/* eslint-disable react-hooks/exhaustive-deps */

import type { JSX } from 'react';
import { useCallback, useMemo, useReducer } from 'react';
import { reportError } from '~/utilities/errorReporting';
import { isNil, isNotNil, isNotUndefined } from '~/utilities/type-guards';
import { noOpFn } from '~/utilities/util';
import type { NotificationStoreActionsType } from '~/components/NotificationTray/NotificationStore';
import {
  getNotificationStoreActions,
  useNotificationStore,
} from '~/components/NotificationTray/NotificationStore';
import { usePromptBeforeNavigation } from '~/hooks/usePromptBeforeNavigation';
import type { Upload } from 'tus-js-client';
import { uploadTitleKey } from './EditableUploadName';
import type { UploaderReducerAction } from './reducer';
import { actions, initialState as initialQueueState, reducer } from './reducer';
import { TusClientErrorResponsePrettifier } from './TusClientErrorResponsePrettifier';
import { UploadContext } from './UploadContext';
import { UploadInstanceTus } from './UploadInstanceTus';
import { useMediaLimit } from './useMediaLimit';
import { defaultOnFileUploaded } from './prebuiltEntityCreators';
import type {
  ApolloClientClientType,
  BoundUploaderFunction,
  BoundUploaderFunctions,
  QueueState,
  UploadContextType,
  UploaderFunction,
  UploaderFunctions,
} from './types';
import { UploadProviderGlobalCallbacks } from './UploadProviderGlobalCallbacks';

export const MAX_MEDIA_LIMIT_COPY = `You're at your media limit and cannot upload more media.`;

const getInitialState = (providedState: QueueState) =>
  Object.entries(providedState).reduce(
    (memo, [containerId, overriddenValues]) => ({
      ...memo,
      [containerId]: {
        ...initialQueueState,
        ...overriddenValues,
      } as QueueState,
    }),
    {},
  );

type UploadList = Record<string, Upload>;
export const CURRENT_UPLOAD_LIST: UploadList = {};
export const UploadProvider = ({
  children,
  initialState = initialQueueState,
}: {
  children: React.ReactNode;
  initialState?: QueueState;
}): JSX.Element => {
  const [state, dispatch] = useReducer(reducer, getInitialState(initialState));
  const {
    mediaCount,
    inflexibleMediaLimit,
    canUploadMoreMedia,
    shouldBlockUpload,
    remainingCount,
  } = useMediaLimit();
  const {
    addTaskToQueue: addNotificationAction,
    updateTaskInQueue: updateNotificationAction,
    setTaskComplete: completeNotificationAction,
    setTaskFailed: failNotificationAction,
    removeTaskFromQueue: removeNotificationAction,
  } = useNotificationStore(getNotificationStoreActions);

  const defaultStateKeys = Object.keys(initialQueueState);
  // We do this in order to get the unique container IDs (as opposed to the default state keys which just includes things like "isUploadStarted")
  const containerIds = Object.keys(state).filter((cid) => !defaultStateKeys.includes(cid));

  const globalIsUploading = containerIds.some((cid) => state[cid].isUploading);

  usePromptBeforeNavigation({ when: globalIsUploading });

  const register = useCallback(
    (cid: string) => {
      if (isNil(state[cid]) && typeof cid === 'string') {
        dispatch(actions.register(cid) as UploaderReducerAction);
      }
    },
    [state],
  );

  const startUpload: UploaderFunctions['startUpload'] = (
    cid,
    { uploadMetadata, onFileUploaded } = {},
  ) => {
    dispatch(
      actions.setOnFileUploaded(
        cid,
        onFileUploaded ? (onFileUploaded as ApolloClientClientType) : defaultOnFileUploaded,
      ),
    );
    if (uploadMetadata !== undefined) {
      dispatch(actions.setUploadMetadata(cid, uploadMetadata));
    }
    dispatch(actions.setIsUploadStarted(cid, true));
  };

  const cancelUpload: UploaderFunctions['cancelUpload'] = useCallback(
    (cid, fileId) => {
      if (isNotUndefined(CURRENT_UPLOAD_LIST[fileId])) {
        void CURRENT_UPLOAD_LIST[fileId].abort().then(() => {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete CURRENT_UPLOAD_LIST[fileId];
          return null;
        });
      }
      dispatch(actions.removeFile(cid, fileId, removeNotificationAction));
      localStorage.removeItem(uploadTitleKey(fileId));
    },
    [removeNotificationAction],
  );

  const setFileDone: UploaderFunctions['setFileDone'] = (cid, { id, attributes }) => {
    dispatch(actions.setFileDone(cid, id, attributes, completeNotificationAction));
  };

  const updateQueue: UploaderFunctions['updateQueue'] = useCallback(
    (
      cid,
      files,
      {
        mediaGroupId,
        listId,
        indexInExistingList,
        onFileUploaded,
        ignoreLimit = false,
        shouldCreateNotification = true,
      } = {},
    ) => {
      dispatch(actions.setNumberOfFilesInUploadAttempt(cid, files.length) as UploaderReducerAction);
      if (shouldBlockUpload(files, ignoreLimit)) {
        const message =
          remainingCount === 0
            ? MAX_MEDIA_LIMIT_COPY
            : `You only have ${remainingCount} media remaining on your plan. Upgrade your plan or try again with fewer media.`;

        dispatch(actions.setMediaLimitExceededMessage(message));
        return setTimeout(() => {
          dispatch(actions.setMediaLimitExceededMessage(''));
        });
      }

      const addNotificationActionImpl: NotificationStoreActionsType['addTaskToQueue'] =
        shouldCreateNotification ? addNotificationAction : noOpFn;

      dispatch(
        actions.addFiles(cid, files, addNotificationActionImpl, cancelUpload, {
          mediaGroupId,
          listId,
          indexInExistingList,
          onFileUploaded,
        }),
      );
      return null;
    },
    [shouldBlockUpload, remainingCount, addNotificationAction, cancelUpload],
  );

  const updateProgress: UploaderFunctions['updateProgress'] = (
    cid,
    fileId,
    { percentageDone, speed, timeRemaining },
  ) =>
    dispatch(
      actions.setProgress(
        cid,
        fileId,
        {
          percentageDone,
          speed,
          timeRemaining,
        },
        updateNotificationAction,
      ),
    );

  const setTusError: UploaderFunctions['setTusError'] = (cid, { id, error }) => {
    dispatch(
      actions.setError(
        cid,
        id,
        TusClientErrorResponsePrettifier.prettify(error) as unknown as Error,
        failNotificationAction,
      ),
    );
  };

  const setCreationError: UploaderFunctions['setCreationError'] = (
    cid,
    { id, error: creationError },
  ) => {
    dispatch(
      actions.setError(cid, id, creationError.message as unknown as Error, failNotificationAction),
    );
  };

  const getQueueState = useCallback(
    (cid: string): QueueState => {
      return isNotNil(state[cid]) ? state[cid] : initialQueueState;
    },
    [state],
  );

  const onTusError: UploaderFunctions['onTusError'] = useCallback((cid, { id, error }) => {
    setTusError(cid, { id, error });
    reportError(error, {
      pillar: 'organize',
      product: 'manage',
    });
  }, []);

  const reset: UploaderFunctions['reset'] = useCallback(
    (cid) => {
      updateQueue(cid, []);
      dispatch(actions.setIsUploadStarted(cid, false));
    },
    [updateQueue],
  );

  const getFunctions = useCallback(
    (cid: string) => {
      const functions: UploaderFunctions = {
        reset,
        cancelUpload,
        startUpload,
        setFileDone,
        setTusError,
        setCreationError,
        updateProgress,
        updateQueue,
        onUploadSuccess: noOpFn,
        onTusError,
        onCreationError: noOpFn,
      };

      return Object.entries(functions).reduce<Partial<UploaderFunctions>>(
        (memo, [name, value]) => ({
          ...memo,
          [name]: (value as UploaderFunction).bind(
            null,
            cid,
          ) as BoundUploaderFunction<UploaderFunction>,
        }),
        {},
      ) as unknown as BoundUploaderFunctions;
    },
    [onTusError, reset, updateQueue],
  );

  const value: UploadContextType = useMemo(
    () => ({
      register,
      getQueueState,
      getFunctions,
      canUploadMoreVideos: canUploadMoreMedia,
      mediaCount,
      inflexibleMediaLimit,
    }),
    [register, getQueueState, getFunctions, canUploadMoreMedia, mediaCount, inflexibleMediaLimit],
  );

  // we need to memoize functions because if we call getFunctions to obtain bound versions on each
  // render the object reference will be difference on each render and UploadIntstance will be
  // rerendered. the immediate effect of this was onProgress being called essentially constantly,
  // and that was locking up the UI.

  // create a primitive key to pass to useMemo that's representative of function changes
  const containerIdsString = containerIds.join(',');
  const functions: Record<string, UploaderFunctions> = useMemo(() => {
    return containerIdsString.split(',').reduce((acc, containerId) => {
      acc[containerId] = getFunctions(containerId);
      return acc;
    }, {});
  }, [containerIdsString, getFunctions]);

  const uploadEntries = Object.entries(state).map(
    ([containerId, { uploadQueue, isUploadStarted }]) => {
      const queueState = getQueueState(containerId);
      if (!isUploadStarted) {
        return null;
      }
      return uploadQueue.map(({ file, isQueued, id, onFileUploaded, ...rest }, index) => (
        <UploadInstanceTus
          id={id}
          file={file}
          key={id}
          index={index}
          isQueued={isQueued}
          onCreationError={functions[containerId].onCreationError}
          onDone={functions[containerId].setFileDone}
          onFileUploaded={onFileUploaded ?? queueState.onFileUploaded}
          onProgress={functions[containerId].updateProgress}
          onSuccess={functions[containerId].onUploadSuccess}
          onTusError={functions[containerId].onTusError}
          projectUUID={queueState.uploadMetadata?.projectUUID}
          uploadMetadata={queueState.uploadMetadata}
          {...rest}
        />
      ));
    },
  );

  return (
    <UploadContext.Provider value={value}>
      <UploadProviderGlobalCallbacks />
      {uploadEntries}
      {children}
    </UploadContext.Provider>
  );
};
